import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { IndexedPhoneButton, PhoneButton } from '../../models/phone-button';
import { ZiroIcons } from '../../models/ziro-icons.enum';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { Phone } from '../../models/generated/ziroModels';
import { DragDropMode } from '../drag-drop-mode.enum';
import { Observable } from 'rxjs';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { PhoneResource } from '../../resources/phone.resource';
import { ToastService } from '../../services/toast.service';
import { map, shareReplay } from 'rxjs/operators';
import { PhoneUiContext } from '../contexts/phone-ui.context';
import { ModalBodyClass } from '../../../modals/modal-body';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { Global360ViewContext } from '../../contexts/global-360-view.context';
import { DetailedModalComponent } from '../../../modals/detailed-modal/detailed-modal.component';
import { AbstractUiContext } from '../contexts/abstract-ui.context';
import { PhoneButtonsEditFormComponent } from '../edit-form/phone-buttons-edit-form.component';
import { TranslateModule } from '@ngx-translate/core';

export interface PhoneButtonsExpansionButtonEditModalViewProperties {
  buttonToEdit: IndexedPhoneButton;
  phone: Phone;
  serverId: string;
  cb: (updatedButton: IndexedPhoneButton) => void;
  phoneUiContext: PhoneUiContext;
}

@Component({
  selector: 'ziro-phone-button-edit-modal',
  templateUrl: './phone-button-edit-modal.component.html',
  styleUrls: ['./phone-button-edit-modal.component.scss'],
  providers: [Global360ViewContext],
  imports: [PhoneButtonsEditFormComponent, TranslateModule],
})
export class PhoneButtonEditModalComponent extends ModalBodyClass implements OnInit {
  private phoneResource = inject(PhoneResource);
  private toastService = inject(ToastService);
  protected _ziroFormStateService: ZiroFormStateService;
  private modalComponent = inject<DetailedModalComponent<any>>(DetailedModalComponent);

  @ViewChild('form') childForm: ZiroFormAbstractDirective<PhoneButton>;
  @ViewChild('errorTemplate') errorTemplate: TemplateRef<any>;

  DragDropMode = DragDropMode;
  ziroIcons = ZiroIcons;
  ZiroFormsValidationState = ZiroFormsValidationState;

  // modal properties passed in from phone-buttons-standard
  buttonToEdit: IndexedPhoneButton;
  phone: Phone;
  serverId: string;
  cb: (updatedButton: IndexedPhoneButton) => void;
  context: AbstractUiContext; // since this is a session-scoped context we cannot inject it into the modal!

  updatedButton: PhoneButton;
  submit$: Observable<void>;
  isValid: ZiroFormsValidationState;
  modalViewProperties: PhoneButtonsExpansionButtonEditModalViewProperties;
  isSubmitting = false;
  isSubmitted = false;
  formConfig = {
    fields: {},
  } as ZiroFormConfig;

  ngOnInit(): void {
    this.setValuesFromViewProperties();
  }

  isFormValid(): boolean {
    return this.childForm.isFormValid();
  }

  setValuesFromViewProperties: () => void = () => {
    this.buttonToEdit = this.modalComponent.modalViewProperties.buttonToEdit;
    this.phone = this.modalComponent.modalViewProperties.phone;
    this.serverId = this.modalComponent.modalViewProperties.serverId;
    this.cb = this.modalComponent.modalViewProperties.cb;
    this.context = this.modalComponent.modalViewProperties.phoneUiContext;
  };

  onFormUpdate(update: ZiroFormsUpdate<PhoneButton>): void {
    this.isValid = update.valid;
    this.updatedButton = update.new;
  }

  submit(): Observable<void> {
    this.isSubmitting = true;
    this.phone.buttons[this.buttonToEdit.index] = this.updatedButton;
    return this.phoneResource.put(this.phone, this.serverId).pipe(
      map(() => {
        this.context.set(this.phone);
        this.toastService.pushSaveToast('Deskphone', this.phone.description, ZiroIcons.DESKPHONE_SMALL);
        this.isSubmitting = false;
        this.cb({ ...this.buttonToEdit, button: this.updatedButton });
      }),
      shareReplay(),
    );
  }
}
